import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchProductCategory, fetchProductTasks, createProductFunction,
} from "../../api/companyApi";
import { fetchFunctionsByRoles } from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function ProductFunctionCreatePage() {
  const { businessDomainId, businessUnitId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [category, setCategory] = useState(null);
  const [productTasks, setProductTasks] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [form, setForm] = useState({
    productFunctionName: "",
    productFunctionDescription: "",
    url: "",
    productTaskId: "",
    productTaskName: "",
    functionId: "",
    functionName: "",
  });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/product-categories/${categoryId}`;

  useEffect(() => {
    (async () => {
      try {
        const cat = await fetchProductCategory(categoryId);
        setCategory(cat);
        const tasks = await fetchProductTasks(cat.productTypeId);
        setProductTasks(Array.isArray(tasks) ? tasks : []);
        try {
          const funcs = await fetchFunctionsByRoles(cat.roles || []);
          setFunctions(Array.isArray(funcs) ? funcs : []);
        } catch { /* market functions may be empty */ }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTaskChange = (e) => {
    const task = productTasks.find((t) => t.productTaskId === e.target.value);
    setForm({
      ...form,
      productTaskId: e.target.value,
      productTaskName: task ? task.productTaskName : "",
    });
  };

  const handleFunctionChange = (e) => {
    const func = functions.find((f) => f.id === e.target.value);
    setForm({
      ...form,
      functionId: e.target.value,
      functionName: func ? func.japaneseName : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.productTaskId) {
      notify.error(t("productFunction.productTask.required"));
      return;
    }
    try {
      const payload = {
        companyId: COMPANY_ID,
        productFunctionName: form.productFunctionName,
        productFunctionDescription: form.productFunctionDescription || null,
        url: form.url || null,
        productCategoryId: categoryId,
        productCategoryName: category?.productCategoryName || "",
        productTaskId: form.productTaskId,
        productTaskName: form.productTaskName,
        functionId: form.functionId || null,
        functionName: form.functionName || null,
      };
      await createProductFunction(payload);
      notify.success(t("message.create.success"));
      navigate(`${basePath}/product-functions`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("productFunction.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>

        {/* 製品タスク */}
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel>{t("productFunction.productTask")}</InputLabel>
          <Select
            value={form.productTaskId}
            onChange={handleTaskChange}
            label={t("productFunction.productTask")}
          >
            {productTasks.map((task) => (
              <MenuItem key={task.productTaskId} value={task.productTaskId}>
                {task.productTaskName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 製品機能名 */}
        <TextField
          fullWidth required
          label={t("productFunction.name")}
          name="productFunctionName"
          value={form.productFunctionName}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        {/* 説明 */}
        <TextField
          fullWidth multiline rows={3}
          label={t("productFunction.description")}
          name="productFunctionDescription"
          value={form.productFunctionDescription}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        {/* URL */}
        <TextField
          fullWidth
          label={t("common.url")}
          name="url"
          value={form.url}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        {/* 機能（market） */}
        {functions.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("productFunction.function")}</InputLabel>
            <Select
              value={form.functionId}
              onChange={handleFunctionChange}
              label={t("productFunction.function")}
            >
              {functions.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.japaneseName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/product-functions`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
